// Code generated by balancegen from data/locale/<tag>/*.toml and
// data/contracts/languages.toml. DO NOT EDIT.
// Regenerate with scripts/gen-data.sh.
//
// One language part of the SOURCE language: some of its tables
// (i18n.md §1.1, R3123). gen/strings.ts imports every part statically
// and joins them, so every page has the English tables with no load.
// The parts keep each build chunk under Vite's limit (deployment.md
// §2.2).
//
// The module imports gen/strings.ts for its types alone. A runtime
// import would make the two chunks import each other.

import type * as S from './strings';

const pluralRules = new Intl.PluralRules('en');

/**
 * Picks the plural variant for a count. The category comes from the
 * platform's CLDR rule for the language of this file, so the file has
 * no rule of its own. `other` is the fallback: it is the one category every language
 * has, and a language that needs no `few` simply has none written.
 */
function plural(n: number, forms: Record<string, string | undefined> & { other: string }): string {
  return forms[pluralRules.select(n)] ?? forms.other;
}

const sonarlab_en: S.Sonarlab = {
  add: (p: { kind: string | number }) => `+ ${p.kind}`,
  add_title: (p: { kind: string | number }) => `add a ${p.kind} at the middle of the map`,
  band_abyss: 'abyss',
  band_deep: 'deep',
  band_shelf: 'shelf',
  band_slope: 'slope',
  card_baffled: (p: { hearing: string | number }) => `This hull is under way. It hears its rear 90° only inside ${p.hearing} of its reach.`,
  card_band: 'depth band',
  card_contacts: 'contacts',
  card_noise: 'noise',
  card_noise_of: (p: { id: string | number }) => `noise of ${p.id}`,
  card_range: 'range',
  card_range_of: (p: { id: string | number }) => `range of ${p.id}`,
  card_thermo: 'thermocline',
  card_typed: 'typed system',
  card_waiting: 'The engine has not answered yet.',
  cause_baffles: 'in the baffles of the listener',
  cause_deep_run: 'a fixed listener does not hear a deep run',
  cause_heard: 'heard',
  cause_hidden: 'the hull hides',
  cause_inert: 'the listener is inert and hears for nobody',
  cause_range: 'out of reach',
  column_hide: 'HIDE COLUMN',
  column_hint: 'the map takes the width',
  column_show: 'SHOW COLUMN',
  copy_link: 'COPY LINK',
  copy_link_title: 'copy a link to this picture',
  defaults: 'DEFAULTS',
  defaults_hint: 'the first lesson',
  dice_title: 'a random seed',
  engine_error: (p: { error: string | number }) => `the sonar engine recovered from a fault: ${p.error}`,
  engine_failed: (p: { error: string | number }) => `the sonar engine failed: ${p.error}`,
  engine_loading: 'the sonar engine loads…',
  engine_missing: (p: { why: string | number }) => `no sonar engine: run scripts/build-battlelab-wasm.sh, then load the page again (${p.why})`,
  engine_ms: (p: { ms: string | number }) => `the engine answered in ${p.ms} ms`,
  engine_no_world: 'the engine gave no world size',
  export_hint: 'every preset, as one file',
  field_more: '3+',
  fit: 'FIT',
  fit_hint: 'frame the units on the screen',
  fold_how: 'how sonar works',
  fold_keys: 'keys',
  fold_presets: 'presets',
  fold_scene: 'lesson',
  fold_unit: 'unit',
  fold_units: 'units',
  fold_world: 'world',
  how_baffles: (p: { hearing: string | number }) => `A hull under way is almost deaf in its rear 90°, the baffles: there it hears a hull only inside ${p.hearing} of its reach. A still hull and a fixed listener hear all round. The wake behind a hull shows where its baffles are.`,
  how_deep_run: 'A fixed listener does not hear a hull on a deep run: a hold, an installation, a Relay Spire and a picket buoy. A hull at sea still hears it.',
  how_depth: (p: { bands: string | number }) => `With the flag depth on, the map has four depth bands. A hull in a deeper band makes less noise: ${p.bands}. A reach from one band into another takes the thermocline factor of the table below, so a reach ring steps at each band edge.`,
  how_field: 'The heard field shows where the watched hull is heard. The page puts a copy of the watched hull, with all its settings, at the middle of each field cell and asks every listener of another faction. A darker tint shows more listeners. At the edge, the distance and the reach are equal.',
  how_fixed: (p: { base: string | number; per: string | number; max: string | number; installation: string | number; spire: string | number; buoy: string | number }) => `A hold hears ${p.base} px, and ${p.per} px more for each Sonar Station level, up to level ${p.max}. A faction installation hears ${p.installation} px, a held Relay Spire ${p.spire} px and a picket buoy ${p.buoy} px.`,
  how_heard: 'A listener hears a hull when the distance between them is shorter than the reach. The reach is the range of the listener, times the noise of the hull, times the thermocline factor of their two depth bands.',
  how_noise: (p: { base: string | number; full: string | number }) => `The noise of a hull starts at the noise figure of its class. A still hull takes ×${p.base} of it, and a hull at full speed takes ×${p.full}.`,
  how_pick: 'the rules',
  how_quiet: (p: { cut: string | number; max: string | number; floor: string | number; damp: string | number; damp_floor: string | number }) => `A captain cuts the noise by ${p.cut} for each point of Quiet Hand, up to ${p.max} points, and never below ×${p.floor}. A Dampening module cuts it again (${p.damp} for tier I, II and III, times its roll), and never below ×${p.damp_floor}.`,
  how_range: (p: { tiers: string | number; roll_min: string | number; roll_max: string | number; ceiling: string | number }) => `A hull hears as far as the range of its class. A Sonar module adds a share of that range: ${p.tiers} for tier I, II and III, times its roll of ${p.roll_min} to ${p.roll_max}. The share of all modules stops at ${p.ceiling}.`,
  how_share: (p: { cls: string | number; share: string | number }) => `An ${p.cls} that shares its array hears ×${p.share} as far, for its whole faction.`,
  how_switches: (p: { silent: string | number; anchor: string | number; gun: string | number; launch: string | number; ping: string | number; crash: string | number; trench: string | number }) => `Silent Running gives a stopped hull ×${p.silent}. An anchor SET gives ×${p.anchor}. A gun echo gives ×${p.gun}, a launch echo ×${p.launch} and a ping echo ×${p.ping}. CRASH DIVE gives ×${p.crash}. A trench gives ×${p.trench}.`,
  how_typed: 'A typed system does not change sonar.',
  import_hint: 'add the presets of a file',
  key_column: 'hide or show the column',
  key_faster: 'speed the motion up by one step',
  key_fit: 'frame the units on the screen',
  key_motion: 'play or pause the motion of the hulls under way',
  key_remove: 'remove the picked unit',
  key_slower: 'slow the motion down by one step',
  kind_buoy: 'picket buoy',
  kind_hold: 'hold',
  kind_hull: 'hull',
  kind_installation: 'installation',
  kind_spire: 'Relay Spire',
  layer_baffles: 'baffles',
  layer_bands: 'thermocline',
  layer_field: 'heard field',
  layer_lines: 'contact lines',
  layer_patrols: 'patrols',
  layer_rings: 'reach ring',
  layer_tags: 'unit tags',
  legend_bands: 'depth bands · noise',
  legend_bands_off: 'depth bands · the flag depth is off',
  legend_field: 'heard by',
  lesson_next: 'NEXT',
  lesson_next_title: 'the next lesson',
  lesson_of: (p: { n: string | number; count: string | number }) => `lesson ${p.n} of ${p.count}`,
  lesson_prev: 'PREV',
  lesson_prev_title: 'the lesson before this one',
  lesson_try: 'try this',
  menu_file: 'presets',
  menu_run: 'motion',
  menu_view: 'view',
  motion_none_title: 'no hull is under way: set one under way in UNIT',
  none: 'none',
  notice_import_bad: (p: { file: string | number }) => `${p.file} is no preset file of the sonar lab`,
  notice_imported: (p: { count: number }) => plural(p.count, {
    one: `${p.count} preset imported`,
    other: `${p.count} presets imported`,
  }),
  notice_link: 'the link is on the clipboard',
  notice_loaded: (p: { name: string | number }) => `${p.name} loaded`,
  notice_no_name: 'type a name first',
  notice_saved: (p: { name: string | number }) => `saved ${p.name}`,
  notice_storage_refused: 'the browser refused the preset',
  pair_dist: (p: { dist: string | number; reach: string | number }) => `distance ${p.dist} px · reach ${p.reach} px`,
  pair_kind: 'contact',
  pair_name: (p: { src: string | number; tgt: string | number }) => `${p.src} → ${p.tgt}`,
  pause: 'PAUSE',
  pct: (p: { n: string | number }) => `${p.n} %`,
  play: 'PLAY',
  preset_delete: 'DELETE',
  preset_delete_sure: 'DELETE?',
  preset_export: 'EXPORT',
  preset_import: 'IMPORT',
  preset_load: 'LOAD',
  preset_name: 'name',
  preset_none: 'no preset yet',
  preset_save: 'SAVE',
  presets_pick: (p: { count: string | number }) => `${p.count} saved`,
  probe_is: 'WATCHED',
  probe_make: 'WATCH THIS HULL',
  probe_title: 'the heard field shows where this hull is heard',
  probe_word: 'watched',
  product: (p: { range: string | number; noise: string | number; thermo: string | number; reach: string | number }) => `${p.range} × ${p.noise} × ${p.thermo} = ${p.reach} px`,
  product_flat: (p: { range: string | number; noise: string | number; reach: string | number }) => `${p.range} × ${p.noise} = ${p.reach} px`,
  remove: 'REMOVE',
  remove_title: 'remove this unit',
  ring_inexact: 'A trench under the watched hull changes its noise, so the page draws no reach ring. The edge of the heard field shows the reach.',
  row_captain: 'captain',
  row_cell: 'field cell',
  row_cell_title: 'the grain of the heard field, in px: a smaller cell draws a finer edge and takes the engine more time',
  row_class: 'class',
  row_class_npc: 'NPC class',
  row_damp: 'Dampening',
  row_damp_roll: 'dampening roll',
  row_depth: 'depth',
  row_depth_title: 'the flag depth: the depth bands, their noise and the thermocline',
  row_faction: 'faction',
  row_module: 'Sonar module',
  row_module_roll: 'module roll',
  row_moving: 'under way',
  row_probe: 'watched hull',
  row_probe_title: 'the heard field shows where this hull is heard',
  row_quiet_hand: 'Quiet Hand',
  row_sapper: 'sapper',
  row_sapper_title: 'the flag sapper: a picket buoy hears only with it on',
  row_scene: 'lessons',
  row_seed: 'seed',
  row_size: 'map size',
  row_station: 'Sonar Station',
  row_typed: 'typed system',
  scene_baffles: 'Baffles',
  scene_deep_run: 'Fixed listeners against a deep run',
  scene_note_baffles: 'A listener under way, and a quiet hull that trails it in its rear arc, where the listener is almost deaf.',
  scene_note_deep_run: 'A hold and a picket buoy lose a hull on a deep run. The hull at sea still hears it.',
  scene_note_one_listener: 'A listener hears a hull of another faction when the hull is inside its reach. The solid line shows that the listener hears the watched hull.',
  scene_note_oracle_share: 'Two Oracles. One Oracle shares its array and hears twice as far.',
  scene_note_overview: 'Everything at once: a hold, an installation, a Relay Spire, a picket buoy and hulls of two factions, two of them under way.',
  scene_note_quiet_hand: 'Two equal hulls before one installation. One hull has a captain with ten points of Quiet Hand.',
  scene_note_thermocline: 'One listener on the shelf. The watched hull runs out to the deep water, and the reach steps down at each band edge.',
  scene_note_under_way: 'Two equal hulls stand at the same distance from the listener. The hull under way makes the noise of its speed, so the listener hears it. The still hull is quiet, and the listener does not hear it.',
  scene_one_listener: 'One listener',
  scene_oracle_share: 'Oracle SHARE',
  scene_overview: 'Overview',
  scene_quiet_hand: 'Quiet Hand',
  scene_thermocline: 'Thermocline',
  scene_try_baffles: 'Drag the quiet hull to the side of the listener, out of the rear arc.',
  scene_try_deep_run: 'Pick the hull on the deep run and turn deep run off in UNIT.',
  scene_try_one_listener: 'Drag the watched hull away from the listener until the line goes dashed.',
  scene_try_oracle_share: 'Pick the Oracle that shares and turn Oracle SHARE off in UNIT.',
  scene_try_overview: 'Pick a contact line to read why the listener hears the hull, or why it does not.',
  scene_try_quiet_hand: 'Pick the hull with the captain and set its Quiet Hand to 0 in UNIT.',
  scene_try_thermocline: 'Pick the listener and watch its reach ring step down at each band edge.',
  scene_try_under_way: 'Press PAUSE. Then drag the still hull nearer to the listener until its line goes solid.',
  scene_under_way: 'Under way',
  sheet_line: (p: { n: string | number; scene: string | number }) => `lesson ${p.n} · ${p.scene}`,
  speed_now: 'now',
  speed_step: (p: { step: string | number }) => `×${p.step}`,
  speed_title: 'the speed of the motion',
  status: (p: { units: string | number; heard: string | number; pairs: string | number }) => `${p.units} units · ${p.heard} of ${p.pairs} contacts heard`,
  still: 'still',
  storage_off: 'this browser keeps no preset, because the storage is off',
  tag_noise: (p: { value: string | number }) => `noise ×${p.value}`,
  tag_range: (p: { px: string | number }) => `hears ${p.px}`,
  term_anchor: 'anchor SET',
  term_buoy: 'picket buoy',
  term_class: 'class',
  term_crash_dive: 'CRASH DIVE',
  term_dampening: 'Dampening',
  term_echo: 'echo',
  term_installation: 'installation',
  term_module: 'Sonar module',
  term_quiet_hand: 'Quiet Hand',
  term_share: 'Oracle SHARE',
  term_silent_running: 'Silent Running',
  term_speed: 'speed',
  term_spire: 'Relay Spire',
  term_station: 'Sonar Station',
  term_terrain: 'depth and trench',
  thermo_caption: 'thermocline factor',
  thermo_corner: 'listener ↓ hull →',
  thermo_pair: (p: { from: string | number; to: string | number }) => `from the ${p.from} into the ${p.to}`,
  title: 'SONAR LAB',
  toggle_anchor: 'anchor SET',
  toggle_crash_dive: 'CRASH DIVE',
  toggle_deep_run: 'deep run',
  toggle_gun_echo: 'gun echo',
  toggle_launch_echo: 'launch echo',
  toggle_ping_echo: 'ping echo',
  toggle_share: 'Oracle SHARE',
  toggle_silent: 'Silent Running',
  toggle_title_anchor: 'the hull stands at anchor',
  toggle_title_crash_dive: 'the hull dives for a short time',
  toggle_title_deep_run: 'the hull runs deep: a fixed listener does not hear it',
  toggle_title_gun_echo: 'the hull has fired its guns a short time ago',
  toggle_title_launch_echo: 'the hull has launched a torpedo a short time ago',
  toggle_title_ping_echo: 'the hull has pinged a short time ago',
  toggle_title_share: 'the Oracle shares its array with its faction',
  toggle_title_silent: 'the hull has Silent Running researched',
  typed_no_sonar: 'A typed system does not change sonar.',
  under_way: 'under way',
  unit_name: (p: { id: string | number; what: string | number }) => `${p.id} · ${p.what}`,
  unit_none: 'Pick a unit on the map or in UNITS.',
  units_count: (p: { n: string | number; max: string | number }) => `${p.n} of ${p.max} units`,
  units_pick: (p: { n: string | number }) => `${p.n} units`,
  value_mul: (p: { mul: string | number }) => `×${p.mul}`,
  value_px: (p: { px: string | number }) => `${p.px} px`,
  verdict_deaf: (p: { cause: string | number }) => `NOT HEARD · ${p.cause}`,
  verdict_heard: 'HEARD',
  world_pick: (p: { seed: string | number; size: string | number }) => `seed ${p.seed} · ${p.size}`,
  zoom: (p: { pct: string | number }) => `${p.pct} %`,
  zoom_in: 'zoom in',
  zoom_out: 'zoom out',
  zoom_title: 'the zoom against a fit',
};

const squad_en: S.Squad = {
  changed_head: 'SQUAD CHANGED',
  changed_none: 'You fly in no squad now. No hull wears the squad mark.',
  changed_pickup: (p: { count: number }) => plural(p.count, {
    one: `You fly in a pickup squad now: ${p.count} commander, you alone. No other hull wears the squad mark.`,
    other: `You fly in a pickup squad now: ${p.count} commanders. The squad mark moved to their hulls and holds.`,
  }),
  changed_squad: (p: { count: number; n: string | number }) => plural(p.count, {
    one: `You fly in squad ${p.n} now: ${p.count} commander, you alone. No other hull wears the squad mark.`,
    other: `You fly in squad ${p.n} now: ${p.count} commanders. The squad mark moved to their hulls and holds.`,
  }),
  chip_pickup: 'PICKUP SQUAD',
  chip_squad: (p: { n: string | number }) => `SQUAD ${p.n}`,
  chip_title: 'Your squad: the commanders whose hulls and holds wear the squad mark. Press for the roster and the key of the marks.',
  faction_hold_no_mark: 'no mark',
  faction_hold_no_plate: 'no plate',
  key_count: (p: { count: number }) => plural(p.count, {
    one: `${p.count} commander`,
    other: `${p.count} commanders`,
  }),
  key_faction_hold: 'FACTION HOLD',
  key_foot: (p: { value: string | number; other: string | number }) => `Marks theme · ${p.value} · the ${p.other} value draws:`,
  key_head: 'MARKS',
  key_rest: 'the rest',
  key_squad_hold: 'SQUAD HOLD',
  key_you: 'YOU',
  key_your_fleets: 'your fleets',
  key_your_hold: 'YOUR HOLD',
  marks_key: 'MARKS KEY',
  open_clan: 'OPEN CLAN',
  own_hold_beam_column: 'a beam column',
  own_hold_breathing_lozenge: 'a breathing lozenge',
  own_hold_cross_rays: 'cross rays',
  own_hold_crown_chevrons: 'crown chevrons',
  own_hold_ground_plate: 'plate with a rim',
  own_hold_landing_pillars: 'landing pillars',
  pickup_body: (p: { count: number }) => plural(p.count, {
    one: `You have no clan, so the world put you in a squad for commanders who have none: ${p.count} commander so far, you. After the season, a squad mate who is a clan officer can invite you.`,
    other: `You have no clan, so the world put you in a squad of ${p.count} commanders who have none. Their hulls wear the squad mark. After the season, a squad mate who is a clan officer can invite you.`,
  }),
  pickup_head: 'PICKUP SQUAD',
  roster_at_sea: 'at sea',
  roster_online: 'online',
  roster_reserve: 'reserve',
  roster_you: 'you',
  squad_hold_half_plate: 'plate, no rim',
  squad_hold_no_mark: 'no mark',
  squad_hold_short_column: 'a short column',
  squad_hold_single_chevron: 'one chevron',
  squad_hold_small_lozenge: 'a small lozenge',
  squad_hold_waterline_ticks: 'waterline ticks',
};

const state_en: S.State = {
  cost_free: 'free',
  dur_days: (p: { days: string | number; hours: string | number }) => `${p.days}d ${p.hours}h`,
  dur_hours: (p: { hours: string | number; min: string | number }) => `${p.hours}h ${p.min}m`,
  dur_minutes: (p: { min: string | number; sec: string | number }) => `${p.min}m ${p.sec}s`,
  dur_seconds: (p: { sec: string | number }) => `${p.sec}s`,
  heat_activity_hi: 'busy',
  heat_activity_label: 'ACTIVITY',
  heat_activity_lo: 'quiet',
  heat_activity_season_hi: 'busy',
  heat_activity_season_label: 'ACTIVITY · SEASON',
  heat_activity_season_lo: 'quiet',
  heat_activity_season_what: 'commander work all season',
  heat_activity_what: 'commander work in the last hours',
  heat_beacon_hi: 'fast',
  heat_beacon_label: 'BEACON',
  heat_beacon_lo: 'none',
  heat_beacon_what: 'sov the anchored Beacons are taking',
  heat_currents_hi: 'fast',
  heat_currents_label: 'CURRENTS',
  heat_currents_lo: 'slack',
  heat_currents_what: 'the speed and the set of the current',
  heat_depth_hi: 'abyss',
  heat_depth_label: 'DEPTH',
  heat_depth_lo: 'shelf',
  heat_depth_what: 'the depth band of each cell',
  heat_kills_hi: 'many',
  heat_kills_label: 'KILLS',
  heat_kills_lo: 'none',
  heat_kills_season_hi: 'many',
  heat_kills_season_label: 'KILLS · SEASON',
  heat_kills_season_lo: 'none',
  heat_kills_season_what: 'fleets lost all season',
  heat_kills_what: 'fleets lost in the last hours',
  heat_off_label: 'OFF',
  heat_off_what: 'the plain map',
  heat_sov_hi: '5',
  heat_sov_label: 'SOV',
  heat_sov_lo: '0',
  heat_sov_what: 'how strongly each owned cell is held',
  heat_stability_hi: 'just flipped',
  heat_stability_label: 'STABILITY',
  heat_stability_lo: 'settled',
  heat_stability_what: 'how recently the cell changed hands',
  mission_names: ['—', 'Move', 'Gather', 'Patrol', 'Hold', 'Hauling', 'Returning', 'Capturing', 'Escorting', 'Hunting', 'Anchored', 'Steering', 'Searching'],
  num_millions: (p: { n: string | number }) => `${p.n}M`,
  num_thousands: (p: { n: string | number }) => `${p.n}k`,
};

const symbols_en: S.Symbols = {
  back: '← THEME WORKSHOP',
  col_hull: 'hull',
  col_kind: 'kind',
  col_role: 'role',
  col_says: 'what the mark says',
  col_weight: 'weight',
  desc_arcs: 'An arc is a piece of a circle. Artillery carries an arc ahead of the bow, the range fan, and the siege gun carries two. A brawler carries an arc astern, a close guard wrapped round the stern. The skirmisher carries two small arcs at the beam, fins. The torpedo boat carries two small arcs ahead, the mouths of its tubes. Each support hull draws its arcs thinner: a ring open at the bow for the Sentinel, one arc astern for the Vedette, two arcs ahead for the Oracle, a full ring for the Tender, three arcs for the Beacon and a cup for the Dredger. Under the hollow master every arc becomes two thin lines and holds.',
  desc_arrows: 'Everything on the water is an arrow. A brawler carries two small darts at its stern beam that point forward, its escorts. Artillery pushes a small dart ahead on the axis, the shell. The skirmisher trails one small dart that points back, the ghost of where it was. The torpedo boat carries two small darts ahead, the torpedoes. Each support hull uses a mini needle: two that point outward for the Sentinel, one reversed for the Vedette, one ahead for the Oracle, one alongside for the Tender, three that radiate for the Beacon and one in tow for the Dredger. One shape makes every mark. Weak point: at the smallest zoom a mini dart astern looks like a second ship behind the first.',
  desc_by_name: 'The Corsair carries a grapnel on a line astern. The Cutlass is the blade, with a crossguard, a grip and a pommel. The Falconet has a short barrel and the two wheels of a carriage gun. The Basilisk has the long barrel and the zigzag tail of a serpent. The Bombard carries a mortar bowl astern. The Lancer has a long lance with a pennon. The Sentinel has a keep astern, the Vedette a pennant, the Oracle an eye ahead of the bow and the Dredger a scoop on a short tow. The Tender keeps its lifebuoy and the Beacon its radial. Two hulls of one role look alike only where the name allows it: the two guns share a barrel, and the two brawlers share nothing. Under the hollow master the pommel, the wheels and the pupil become rings.',
  desc_chevrons: 'A chevron is two bars that meet at a point, the rank mark of every navy. A brawler stacks chevrons astern that point forward, the drive that pushes it in. Artillery stacks them ahead of the bow, the shell going out. The skirmisher trails one chevron that points back. The torpedo boat carries two small chevrons at the beam. The count is the weight. Each support hull carries one thin chevron in a shape of its own: an A-frame for the Sentinel, a small back chevron for the Vedette, a V at the bow for the Oracle, a brace for the Tender, a burst for the Beacon and a closed triangle on a tow for the Dredger. The hollow master holds, because a chevron is already two lines.',
  desc_default: 'This is the set the game ships. A combat hull wears one swept wing on each side. The sweep of the wing says how the ship fights, and the span of the wing says how heavy it is. Each support hull wears a thin ornament that names its trade: two swept bars for the Sentinel, a quill for the Vedette, two arcs for the Oracle, a ring for the Tender, a radial for the Beacon and a tow of crates for the Dredger. Weak point: the two detached bars of the Sentinel look much like a swept wing.',
  desc_filigree: 'The combat row drops to the thin line of the support row. The Corsair carries two hairline streaks astern. The Cutlass carries two pairs of hairline stubs at the bow. The Falconet carries a hairline barrel with one tick, and the Basilisk a longer barrel with two. The Bombard carries a hairline barrel and a hairline ring ahead, the splash. The Lancer carries a stub and three dots ahead, the track. The support row is the shipped one. This is the lightest set, and the dart under the hairlines carries the difference between the two rows alone. Weak point: at the smallest zoom a hairline is gone and the combat row is plain darts.',
  desc_insignia: 'The quietest set. The Corsair carries one slash astern and the Cutlass an X. The Falconet carries one bar ahead of the bow and the Basilisk two. The Bombard carries a solid disc astern. The Lancer carries a line that ends in a dot. The Sentinel carries a hollow square, the Vedette one hairline, the Oracle a dot in a ring, the Tender a cross, the Beacon an asterisk and the Dredger a solid square. The kinship is in the position: a gun carries its mark ahead of the bow, and every other hull carries it astern. You learn the set in a minute, but no mark tells you what the hull does before you learn it. Under the hollow master the disc and the square become a ring and a box, and the pupil of the Oracle is lost.',
  desc_pips: 'A pip is one solid dot. A brawler carries pairs of pips at the bow beam, the knuckles of a fist. Artillery carries pips in a line ahead of the bow, the shell in flight. The skirmisher trails one pip astern, a wake. The torpedo boat carries two pips ahead, its tubes. Each support hull uses a smaller pip: three astern for the Sentinel, one for the Vedette, three in an arc for the Oracle, four in a ring for the Tender, five in a radial for the Beacon and two in a tow line for the Dredger. The set reads best at the smallest zoom. Weak point: under the hollow master each pip becomes a small ring, and the small support pips blur together.',
  desc_plates: 'A plate is a flat piece beside the hull. One rule runs the whole roster: a combat hull carries its plate solid, and a support hull carries its plate as an outline. A brawler wears square armour at the beam. Artillery wears long thin plates beside the bow, the barrels. The skirmisher wears two small fins at the stern. The torpedo boat wears two plates shaped like a bullet. The size of the plate is the weight. Weak point: under the hollow master a combat plate becomes a box, which is what the support row already is, so the two rows stand closer.',
  desc_rungs: 'A rung is one bar across the hull. A brawler carries square rungs at the bow, because it closes with the enemy. Artillery carries square rungs astern, because it stands off. The skirmisher has one rung swept back, and the torpedo boat has rungs swept forward. The count of the rungs is the weight: one, two or three. Each support hull carries one thin rung with a tip that names its trade. Under the hollow master a rung becomes a thin box, and the count still reads.',
  desc_tails: 'The bow is the base and the tail is the class. A brawler ends in a blunt block astern. Artillery trails a long thin tail, the recoil, and the siege gun ends it with a breech bar. The skirmisher has a forked tail, a fish. The torpedo boat trails twin tails, its tubes. Each support hull trails a thin tail: a T for the Sentinel, a short V for the Vedette, a line of pings for the Oracle, a loop for the Tender, a fan for the Beacon and a crate on a tow for the Dredger. Every mark is longest astern, so the bow stays the clearest point and you read the heading from the tail too. Under the hollow master the pings of the Oracle become rings.',
  desc_teeth: 'A tooth is a small triangle on the flank of the hull, so the outline is serrated. A brawler wears teeth swept back from the bow. Artillery wears square teeth. The skirmisher wears one pair swept far back. The torpedo boat wears teeth swept forward. The count is one, two or three pairs. Each support hull wears tiny teeth in a shape of its own: battlements for the Sentinel, one swept pair for the Vedette, a V for the Oracle, a dock for the Tender, a star for the Beacon and a rake for the Dredger. The set is busiest at the largest zoom and calmest at the smallest, where the teeth fuse into a wider hull. Under the hollow master the teeth become open notches and the count still reads.',
  form_desk: 'DESKTOP',
  form_phone: 'TELEPHONE',
  grammar: 'grammar',
  grammar_arcs: 'the role is which way the arc faces · the weight is the radius and the count · a support hull draws its arcs thinner',
  grammar_arrows: 'the role is where the small arrow sits and which way it points · the weight is its size and count · a support hull wears mini needles',
  grammar_by_name: 'each hull draws the thing it is named after',
  grammar_chevrons: 'the role is where the chevron sits and which way it points · the weight is the count · a support hull wears one thin chevron',
  grammar_default: 'the role is the sweep of the wing · the weight is the span of the wing · a support hull wears a thin ornament',
  grammar_filigree: 'every extra is a hairline, and the dart alone carries the weight',
  grammar_insignia: 'each hull carries one small mark of the fewest lines',
  grammar_pips: 'the role is where the pips sit · the weight is the count of the pips · a support hull wears smaller pips',
  grammar_plates: 'the role is the shape of the plate beside the hull · the weight is the size of the plate · a support hull wears the same plate as an outline',
  grammar_rungs: 'the role is where the rung sits and how it sweeps · the weight is the count of the rungs · a support hull wears one thin rung',
  grammar_tails: 'the role is the shape of the tail · the weight is the length of the tail · a support hull wears a thin tail',
  grammar_teeth: 'the role is the sweep of the teeth on the flank · the weight is the count of the teeth · a support hull wears tiny teeth',
  ink_colour: 'COLOUR',
  ink_grey: 'GREYSCALE',
  kind_combat: 'combat',
  kind_support: 'support',
  lede: 'A ship icon set is the look of every ship on the map. The SHIP ICON row of the theme workshop picks one set for every hull. A PER HULL row gives one hull a set of its own. This page shows every set on every hull.',
  master_fill: 'FILLED',
  master_hollow: 'HOLLOW',
  role_artillery: 'artillery',
  role_brawler: 'brawler',
  role_cargo: 'cargo',
  role_command: 'command',
  role_engineer: 'engineer',
  role_repair: 'repair',
  role_scout: 'scout',
  role_skirmish: 'skirmish',
  role_sonar: 'sonar',
  role_sovereignty: 'sovereignty',
  role_torpedo: 'torpedo',
  role_tower: 'tower',
  rules_bases: 'Two shapes carry the whole roster. Every combat mark is one dart plus extras. Every support mark is one thin needle plus extras. A set states the extras alone, so no set can break the rule.',
  rules_title: 'THE RULES EVERY SET KEEPS',
  rules_weight: 'A combat mark says the role and the weight. The role is the way the ship fights. The weight has three steps: light, medium and heavy. Every support mark is thin. The MASTER switch shows the two ways the game paints a mark. It fills the shape for most hulls and strokes it for a squadmate, so a dot becomes a small ring and a solid block becomes a box. A set that loses a detail that way says so.',
  says_basilisk: 'the siege gun',
  says_beacon: 'it plants and broadcasts; territory moves because of it',
  says_bombard: 'the heaviest hull',
  says_commodore: 'the one flagship; it leads the own hulls round it',
  says_corsair: 'the fast quiet hunter',
  says_cutlass: 'the cheap fist, the free hull',
  says_dredger: 'the hauler',
  says_falconet: 'the cheap gun',
  says_lancer: 'the striker; three tube kinds',
  says_oracle: 'the ear; it shares what it hears',
  says_sapper: 'the engineer; it lays sea mines and deployables and takes them back aboard',
  says_sentinel: 'the one support hull that shoots; it plants and holds a lane',
  says_tender: 'it mends the hull beside it; set, it is a berth',
  says_vedette: 'the eyes; the quietest hull',
  sw_form: 'form',
  sw_ink: 'ink',
  sw_master: 'master',
  sw_view: 'view',
  sw_zoom: 'zoom',
  title: 'SHIP SYMBOLS',
  view_sets: 'BY SET',
  view_ships: 'BY SHIP',
  weight_heavy: 'heavy',
  weight_light: 'light',
  weight_medium: 'medium',
  weight_thin: 'thin',
  zoom_half: '0.5×',
  zoom_one: '1×',
  zoom_two: '2×',
};

const terrainlab_en: S.Terrainlab = {
  apron_value: 'of a strategic object',
  cache_value: (p: { id: string | number }) => `seeded cache ${p.id}`,
  card_anchor: 'Anchor',
  card_cache: (p: { id: string | number }) => `Seeded cache ${p.id}`,
  card_cell: (p: { cell: string | number }) => `Cell ${p.cell}`,
  card_den: (p: { id: string | number }) => `Den ${p.id}`,
  card_deposit: (p: { resource: string | number }) => `${p.resource} deposit`,
  card_hold: (p: { pocket: string | number }) => `Hold of pocket ${p.pocket}`,
  card_lair: (p: { id: string | number }) => `Lair ${p.id}`,
  card_note: 'A tap on a cell picks it. The pick stays when the seed moves, so A and B show the same cell.',
  card_pocket: (p: { pocket: string | number }) => `Squad pocket ${p.pocket}`,
  card_point: (p: { kind: string | number; id: string | number }) => `${p.kind} ${p.id}`,
  card_seamount: (p: { id: string | number }) => `Seamount ${p.id}`,
  cell_place: (p: { cell: string | number; col: string | number; row: string | number }) => `${p.cell} · col ${p.col} · row ${p.row}`,
  cells_chip: (p: { cells: string | number }) => `${p.cells} cells`,
  copy_ab: 'A → B',
  copy_ab_hint: 'copy A into B',
  copy_ab_title: 'copy the generators and the values of A into B',
  copy_link: 'COPY LINK',
  default_value: (p: { value: string | number }) => `default ${p.value}`,
  dice_title: 'a random seed',
  editing: (p: { side: string | number }) => `editing side ${p.side}`,
  export_hint: 'all, as one file',
  fact_ambush: 'AMBUSH WATER',
  fact_amount: 'AMOUNT',
  fact_apron: 'APRON',
  fact_body: 'TRENCH BODY',
  fact_brotherhood: 'BROTHERHOOD',
  fact_buoy: 'BUOY',
  fact_cache: 'CACHE',
  fact_cell: 'CELL',
  fact_design: 'DESIGN',
  fact_faction: 'FACTION',
  fact_fairway: 'FAIRWAY',
  fact_holds: 'HOLDS',
  fact_hunt: 'HUNTING WATER',
  fact_monster: 'MONSTER',
  fact_moved: 'DEN',
  fact_owner: 'TERRITORY',
  fact_pirate_cache: 'PIRATE CACHE',
  fact_quiet: 'QUIET',
  fact_seamount: 'SEAMOUNT',
  fact_sortie: 'SORTIE WATER',
  fact_terrain: 'TERRAIN',
  fact_window: 'WINDOW',
  failed: (p: { side: string | number; error: string | number }) => `side ${p.side}: the generation failed: ${p.error}`,
  fairway_value: (p: { index: string | number }) => `chain ${p.index}`,
  fit: 'FIT',
  hunt_value: (p: { brotherhood: string | number; id: string | number }) => `${p.brotherhood} den ${p.id}`,
  hunt_value_own: (p: { brotherhood: string | number; id: string | number }) => `${p.brotherhood} den ${p.id} · a target here comes first`,
  import_hint: 'add a file',
  key_dice: 'a random seed',
  key_fit: 'fit the map to the screen',
  key_next: 'the seed after',
  key_panel: 'open or close the panel',
  key_prev: 'the seed before',
  key_split: 'show A and B side by side',
  key_swap: 'edit and show the other side',
  keys: 'keys',
  kind_anchor: 'FACTION ANCHOR',
  kind_cache: 'SEEDED CACHE',
  kind_cell: 'CELL',
  kind_den: 'PIRATE DEN',
  kind_den_quiet: 'PIRATE DEN · QUIET',
  kind_deposit: 'DEPOSIT',
  kind_hold: 'HOLD',
  kind_hold_fallback: 'HOLD · NO FLOOR, TOOK THE CELL UNDER THE CENTRE',
  kind_installation: 'FACTION INSTALLATION',
  kind_lair: 'MONSTER LAIR',
  kind_object: 'STRATEGIC OBJECT',
  kind_pocket: 'SQUAD POCKET',
  kind_pocket_fallback: 'SQUAD POCKET · FELL BACK ONTO THE ANCHOR',
  kind_pocket_installation: 'SQUAD POCKET · ON AN INSTALLATION',
  kind_pocket_middle: 'SQUAD POCKET · MIDDLE BAND',
  kind_point: 'POINT OF INTEREST',
  kind_seamount: 'SEAMOUNT',
  layer_anchors: 'anchors',
  layer_buoys: 'buoys',
  layer_caches: 'caches',
  layer_dens: 'pirate dens',
  layer_fairways: 'fairways',
  layer_grid: 'hex grid',
  layer_holds: 'holds',
  layer_hunt: 'hunting and sortie water',
  layer_installations: 'faction installations',
  layer_lairs: 'monster lairs',
  layer_objects: 'strategic objects',
  layer_pockets: 'squad pockets',
  layer_points: 'vents and current gates',
  layer_quiet: 'quiet dens',
  layer_ranges: 'monster ranges',
  layer_seamounts: 'seamounts',
  layer_sites: 'deposits',
  layer_territory: 'territory',
  menu_lab: 'THIS LAB',
  menu_presets: 'PRESETS',
  moved_value: (p: { id: string | number; cell: string | number }) => `den ${p.id} moves here from cell ${p.cell}`,
  ms: (p: { ms: string | number }) => `${p.ms} ms`,
  name: 'Terrain lab',
  next_title: 'the seed after',
  no_generator: 'this generation slot has no generator',
  none: 'none',
  notice_import_bad: (p: { file: string | number }) => `${p.file} is no preset file of the terrain lab`,
  notice_imported: (p: { count: number }) => plural(p.count, {
    one: `${p.count} preset imported`,
    other: `${p.count} presets imported`,
  }),
  notice_link: 'the link is on the clipboard',
  notice_loaded: (p: { name: string | number; side: string | number }) => `${p.name} loaded into side ${p.side}`,
  notice_no_name: 'type a name first',
  notice_saved: (p: { name: string | number }) => `saved ${p.name}`,
  notice_storage_refused: 'the browser refused the preset',
  overlay: 'cell field',
  overlay_height: 'height',
  panel: 'PANEL',
  panel_hint: 'the column',
  panel_title: 'open or close the panel',
  pirate_cache_value: (p: { id: string | number; x: string | number; y: string | number }) => `den ${p.id} · x ${p.x} · y ${p.y}`,
  pirates: 'PIRATES',
  pirates_off: 'off',
  pirates_on: 'on',
  pirates_title: 'the flag pirates: on, a lair has a monster kind and a den a brotherhood, and the dens think',
  preset_delete: 'DELETE',
  preset_delete_sure: 'DELETE?',
  preset_export: 'EXPORT',
  preset_import: 'IMPORT',
  preset_load: 'LOAD',
  preset_load_title: (p: { side: string | number }) => `load this preset into side ${p.side}; the seed stays`,
  preset_name: 'name',
  preset_none: 'no preset yet',
  preset_save: (p: { side: string | number }) => `SAVE ${p.side}`,
  preset_save_title: (p: { side: string | number }) => `save the generators and the values of side ${p.side} under this name`,
  preset_seed: (p: { seed: string | number; seats: string | number }) => `seed ${p.seed} · ${p.seats}`,
  preset_seed_title: 'use the seed and the size this preset was saved at',
  presets: 'presets',
  presets_pick: (p: { count: number }) => plural(p.count, {
    one: `${p.count} saved · in this browser`,
    other: `${p.count} saved · in this browser`,
  }),
  prev_title: 'the seed before',
  quiet_stays: (p: { rings: string | number }) => `no free cell inside ${p.rings} rings; stays quiet`,
  quiet_to: (p: { cell: string | number }) => `moves to cell ${p.cell}`,
  range_value: (p: { id: string | number; kind: string | number }) => `lair ${p.id} · ${p.kind}`,
  roll: 'roll seed',
  roll_bad: 'a roll seed is empty or a whole number from -9223372036854775808 to 9223372036854775807',
  roll_dice_title: 'a random roll seed',
  roll_empty: '= seed',
  roll_title: 'the roll seed of the points of interest, the dens and the NPC draws; empty takes the seed',
  seamount_value: (p: { id: string | number; cells: string | number }) => `seamount ${p.id} · ${p.cells} cells`,
  seed: 'seed',
  seed_bad: 'a seed is a whole number from -9223372036854775808 to 9223372036854775807',
  side_a: 'A',
  side_b: 'B',
  side_title: (p: { side: string | number }) => `edit and show side ${p.side}`,
  size: 'size',
  size_row: (p: { seats: string | number; cols: string | number; rows: string | number }) => `${p.seats} seats · ${p.cols} × ${p.rows}`,
  slot_cleanup: 'cleanup',
  slot_fairways: 'fairways',
  slot_placement: 'placement',
  slot_sites: 'sites',
  slot_terrain: 'terrain',
  sortie_value: (p: { brotherhood: string | number; id: string | number; every: string | number }) => `${p.brotherhood} den ${p.id} · one roam leg in ${p.every}`,
  split: 'SPLIT',
  split_title: 'show A and B side by side, on one seed',
  stats: 'stats',
  stats_ab: 'A against B',
  stats_pick: (p: { ms: string | number }) => `${p.ms} ms`,
  status_cells: (p: { cells: string | number }) => `${p.cells} cells`,
  status_failed: (p: { side: string | number }) => `${p.side} failed`,
  status_fallbacks: (p: { count: number; side: string | number }) => plural(p.count, {
    one: `${p.side} ${p.count} fallback`,
    other: `${p.side} ${p.count} fallbacks`,
  }),
  storage_off: 'this browser keeps no preset, because the storage is off',
  terrain_height: (p: { terrain: string | number; height: string | number }) => `${p.terrain} · height ${p.height}`,
  timings: 'time',
  tip_ambush: (p: { id: string | number; kind: string | number }) => `ambush water of lair ${p.id} · ${p.kind}`,
  tip_anchor: (p: { faction: string | number }) => `anchor · ${p.faction}`,
  tip_apron: 'apron of a strategic object',
  tip_body: (p: { id: string | number; kind: string | number }) => `trench body of lair ${p.id} · ${p.kind}`,
  tip_buoy: (p: { kind: string | number }) => `buoy · ${p.kind}`,
  tip_cache: (p: { id: string | number }) => `seeded cache ${p.id}`,
  tip_cell: (p: { cell: string | number; col: string | number; row: string | number }) => `cell ${p.cell} · col ${p.col} · row ${p.row}`,
  tip_den: (p: { id: string | number; brotherhood: string | number; design: string | number }) => `den ${p.id} · ${p.brotherhood} · ${p.design}`,
  tip_den_moved: (p: { id: string | number; cell: string | number }) => `den ${p.id} moves here from cell ${p.cell}`,
  tip_den_plain: (p: { id: string | number }) => `den ${p.id}`,
  tip_den_quiet: (p: { id: string | number; cell: string | number }) => `den ${p.id} is quiet · moves to cell ${p.cell}`,
  tip_den_stays: (p: { id: string | number }) => `den ${p.id} is quiet · no free cell, stays quiet`,
  tip_deposit: (p: { resource: string | number; amount: string | number }) => `deposit · ${p.resource} · ${p.amount}`,
  tip_fairway: (p: { index: string | number }) => `fairway chain ${p.index}`,
  tip_hold: (p: { faction: string | number; pocket: string | number }) => `hold · ${p.faction} · squad pocket ${p.pocket}`,
  tip_hold_fallback: (p: { faction: string | number; pocket: string | number }) => `hold · ${p.faction} · squad pocket ${p.pocket} · no floor, took the cell under the centre`,
  tip_hunt: (p: { brotherhood: string | number; id: string | number }) => `hunting water of ${p.brotherhood} den ${p.id}`,
  tip_hunt_own: (p: { brotherhood: string | number; id: string | number }) => `hunting water of ${p.brotherhood} den ${p.id} · a target here comes first`,
  tip_installation: (p: { name: string | number; faction: string | number }) => `faction installation · ${p.name} · ${p.faction}`,
  tip_lair: (p: { id: string | number; kind: string | number; design: string | number }) => `lair ${p.id} · ${p.kind} · ${p.design}`,
  tip_lair_plain: (p: { id: string | number }) => `lair ${p.id}`,
  tip_object: (p: { name: string | number; window: string | number }) => `strategic object · ${p.name} · window ${p.window}`,
  tip_owner: (p: { faction: string | number }) => `territory · ${p.faction}`,
  tip_pirate_cache: (p: { id: string | number }) => `pirate cache of den ${p.id}`,
  tip_pocket: (p: { pocket: string | number; faction: string | number; members: string | number }) => `squad pocket ${p.pocket} · ${p.faction} · ${p.members} holds`,
  tip_pocket_fallback: (p: { pocket: string | number; faction: string | number }) => `squad pocket ${p.pocket} · ${p.faction} · fell back onto the anchor`,
  tip_pocket_installation: (p: { pocket: string | number; faction: string | number }) => `squad pocket ${p.pocket} · ${p.faction} · on an installation`,
  tip_pocket_middle: (p: { pocket: string | number; faction: string | number }) => `squad pocket ${p.pocket} · ${p.faction} · middle band`,
  tip_point: (p: { id: string | number; kind: string | number }) => `point of interest ${p.id} · ${p.kind}`,
  tip_ridge: 'ridge',
  tip_seamount: (p: { id: string | number; cells: string | number }) => `seamount ${p.id} · ${p.cells} cells`,
  tip_sortie: (p: { brotherhood: string | number; id: string | number }) => `sortie water of ${p.brotherhood} den ${p.id}`,
  tip_trench: 'trench',
  tip_value: (p: { name: string | number; value: string | number }) => `${p.name} ${p.value}`,
  tip_water: 'water',
  total: 'total',
};

const timelapse_en: S.Timelapse = {
  band: 'TIMELAPSE',
  legend_commander: (p: { commander: string | number }) => `The faction of ${p.commander}`,
  legend_head: 'CELLS HELD AT THE FRAME',
  legend_mine: 'Your faction',
  live: 'LIVE',
  on_the_map: '▸ ON THE MAP',
  pause: 'Pause',
  play: 'Play',
  reader_frames: (p: { frames: number }) => plural(p.frames, {
    one: `${p.frames} FRAME`,
    other: `${p.frames} FRAMES`,
  }),
  reader_head: (p: { from: string | number; to: string | number }) => `THE SEA · DAY ${p.from} TO DAY ${p.to}`,
  scrub: 'The frame',
  series_tag: 'TIMELAPSE',
  speed: (p: { n: string | number }) => `×${p.n}`,
  speed_title: 'The speed',
  stamp: (p: { day: string | number; time: string | number }) => `DAY ${p.day} · ${p.time}`,
  stamp_now: (p: { day: string | number }) => `NOW · DAY ${p.day}`,
  summary_every: (p: { hours: number }) => plural(p.hours, {
    one: `ONE FRAME EVERY ${p.hours} HOUR`,
    other: `ONE FRAME EVERY ${p.hours} HOURS`,
  }),
  summary_head: (p: { frames: number }) => plural(p.frames, {
    one: `THE SEASON · ${p.frames} FRAME`,
    other: `THE SEASON · ${p.frames} FRAMES`,
  }),
  tick_day: (p: { day: string | number }) => `DAY ${p.day}`,
  tick_now: 'NOW',
  war_head: (p: { stamp: string | number }) => `Sovereignty · ${p.stamp}`,
  war_note: 'Drag the frame or press play: the map shows who held each cell at that time. Drag to NOW for the live map.',
};

const touchLog_en: S.TouchLog = {
  chip: (p: { count: string | number }) => `TOUCH LOG · ${p.count}`,
  clear: 'CLEAR',
  copied: 'COPIED',
  copy: 'COPY',
  select_and_copy: 'SELECT THE TEXT AND COPY IT',
  share: 'SHARE',
};

const trials_en: S.Trials = {
  closing_head: 'SEA TRIALS COMPLETE',
  closing_long: 'Next: take cells with your Beacons. Each cell they take adds to your season score.',
  closing_short: (p: { done: string | number; total: string | number }) => `${p.done} / ${p.total}`,
  drill: 'learn the controls in a drill. Fleets, sonar and fights work as in conquest, and a drill uses no arcade run.',
  drill_go: 'START',
  drill_tag: 'DRILL',
  drill_title: 'Opens the drill in the arcade',
  fold: 'Fold the sea trials to one line',
  label: (p: { done: string | number; total: string | number }) => `TRIALS ${p.done}/${p.total}`,
  next: (p: { step: string | number }) => `${p.step}.`,
  show: 'SHOW',
  show_title: (p: { panel: string | number }) => `Opens ${p.panel} and gives no order`,
  step_build_beacon: 'Build a Beacon',
  step_build_dredger: 'Build a Dredger',
  step_launch_fleet: 'Launch a fleet',
  step_queue_building: 'Build a building',
  step_recruit_captain: 'Recruit a captain',
  step_send_gather: 'Send a fleet to gather',
  step_start_research: 'Decrypt a tech research node',
  step_take_cell: 'Take a cell',
  title: 'Open the panel of the next act. The sea trials give no order.',
  unfold: 'Show the eight acts',
};

const viewpanel_en: S.Viewpanel = {
  back: 'back to VIEW',
  chart_aside: 'the reading of the furniture',
  chart_head: 'CHART',
  chat: 'CHAT',
  group_currents: 'CURRENTS',
  group_depth: 'DEPTH',
  group_names: 'NAMES',
  group_tracks: 'TRACKS',
  group_wrecks: 'WRECKS',
  head: 'VIEW',
  head_sub: 'what stands over the map',
  heat: 'HEAT',
  heat_aside_touch: 'one press per mode',
  heat_none_what: 'No mode paints.',
  heat_off: 'OFF',
  heat_paints: (p: { mode: string | number }) => `${p.mode} paints`,
  heat_row: (p: { mode: string | number }) => `HEAT · ${p.mode}`,
  home: 'GO TO THE HOLD',
  key: 'FACTION KEY',
  layers: 'LAYERS',
  legend: 'LEGEND',
  map: 'MINIMAP',
  off: 'OFF',
  on: 'ON',
  pieces_head: 'CHART PIECES',
  reading_historical: 'HISTORICAL',
  reading_modern: 'MODERN',
  reading_off: 'OFF',
  rose_peek: 'HOME',
  rose_title: 'centre the map on your hold',
  scale_cells: (p: { n: number }) => plural(p.n, {
    one: `${p.n} cell`,
    other: `${p.n} cells`,
  }),
  sea: 'SEA VIEW HERE',
  sea_follow: 'FOLLOW IN THE SEA VIEW',
  unread: (p: { n: number }) => plural(p.n, {
    one: `${p.n} unread chat line`,
    other: `${p.n} unread chat lines`,
  }),
  view: 'VIEW',
  view_title: 'what stands over the map · hold for the quick wheel',
};

const warcharts_en: S.Warcharts = {
  unclaimed: 'Unclaimed',
};

const welcome_en: S.Welcome = {
  built: 'WHAT THE AUTOPILOT BUILT',
  built_buildings: (p: { n: number }) => plural(p.n, {
    one: `${p.n} BUILDING`,
    other: `${p.n} BUILDINGS`,
  }),
  built_captains: (p: { n: number }) => plural(p.n, {
    one: `${p.n} CAPTAIN`,
    other: `${p.n} CAPTAINS`,
  }),
  built_fleets: (p: { n: number }) => plural(p.n, {
    one: `${p.n} FLEET`,
    other: `${p.n} FLEETS`,
  }),
  captain_ashore: 'ashore',
  command: (p: { hold: string | number }) => `You command one empire: the hold ${p.hold}, its buildings, its fleets and its captains.`,
  dir_e: 'east',
  dir_n: 'north',
  dir_ne: 'north-east',
  dir_nw: 'north-west',
  dir_s: 'south',
  dir_se: 'south-east',
  dir_sw: 'south-west',
  dir_w: 'west',
  find_clan: 'FIND A CLAN',
  find_clan_title: 'Opens CLAN · FIND A CLAN of the hub in a new tab',
  fleet_pens: (p: { cls: string | number }) => `${p.cls} · pens`,
  go: 'TAKE COMMAND',
  handover: 'The autopilot held your empire since the season began. It stands down now.',
  head_buildings: 'BUILDINGS',
  head_captains: 'CAPTAINS',
  head_fleets: 'FLEETS',
  head_neighbours: 'NEIGHBOURS',
  head_research: 'RESEARCH',
  head_season: 'THE SEASON',
  heading: 'YOU TAKE COMMAND',
  how: 'HOW IT PLAYS',
  kicker: (p: { season: string | number; day: string | number; faction: string | number }) => `SEASON ${p.season} · DAY ${p.day} · ${p.faction}`,
  level: (p: { level: string | number }) => `level ${p.level}`,
  neighbour: (p: { faction: string | number; dir: string | number }) => `${p.faction} · ${p.dir}`,
  neighbour_own: (p: { dir: string | number }) => `your faction · ${p.dir}`,
  research_done: 'done',
  research_queued: 'in the queue',
  season_clan: (p: { members: string | number }) => `A clan of up to ${p.members} commanders keeps you together after this season.`,
  season_goal: (p: { days: string | number }) => `Goal: grow your season score. Each cell your Beacons take, each hostile fleet you sink and each siege you win adds points. When the ${p.days}-day season ends, the score becomes Honour.`,
  season_stays: 'What stays: your captains, your Honour and your name. Everything else ends at the seaquake.',
  season_team: 'You play with your squad and your faction. They share the ground, the chat and the fight for the strategic objects.',
  squad: (p: { squad: string | number; clan: string | number }) => `You fly in squad ${p.squad} of the ${p.clan}.`,
  threat: (p: { hold: string | number; faction: string | number; dir: string | number }) => `The nearest hostile hold is ${p.hold}, ${p.faction}, to the ${p.dir}.`,
  threat_time: (p: { fleet: string | number; time: string | number }) => `${p.fleet}, your fastest fleet, needs about ${p.time} to reach it.`,
  trials: 'SEA TRIALS',
  truce: (p: { left: string | number }) => `Founding truce: nobody can attack you for ${p.left}.`,
  truce_over: 'The founding truce is over. A hostile fleet can attack you now.',
  where_advisor: 'The ADVISOR line names one good next step. A press opens the panel. You give the order.',
  where_key: 'The faction colours: VIEW · LEGEND.',
  where_support: 'Questions: ☸ OPTIONS · MENU · HELP.',
};

const workshop_en: S.Workshop = {
  activated: (p: { code: string | number }) => `CODE ${p.code} IS ACTIVE`,
  add: '+ ADD',
  alpha_edge: 'EDGE ALPHA',
  alpha_panel: 'PANEL ALPHA',
  back_hub: '← HUB',
  bank: 'THE BANK',
  cancel: 'CANCEL',
  chart_changed: 'A switch with a gold edge differs from the build.',
  chart_lede: 'A Chart theme turns a piece on or off in one reading. The theme stores only the switches that differ from the build.',
  chart_pack: (p: { historical: string | number; modern: string | number }) => `BUILD ${p.historical} · ${p.modern}`,
  chart_reading: 'PREVIEW READING',
  chart_switch: (p: { reading: string | number; value: string | number }) => `${p.reading} ${p.value}`,
  chk_contrast: (p: { ratio: string | number; floor: string | number }) => `Text on the panel reads ${p.ratio} : 1, below the floor of ${p.floor} : 1. Make the text lighter or the panel darker.`,
  chk_faint_panel: (p: { ratio: string | number; floor: string | number }) => `Faint text on the panel reads ${p.ratio} : 1, below the floor of ${p.floor} : 1. Make the faint text lighter or the panel darker.`,
  chk_faint_sheet: (p: { ratio: string | number; floor: string | number }) => `Faint text on the sheet reads ${p.ratio} : 1, below the floor of ${p.floor} : 1. Make the faint text lighter or the sheet darker.`,
  clip_gone: 'CLIP NOT IN THE BANK',
  clip_label: (p: { n: string | number }) => `CLIP ${p.n}`,
  confirm_links: (p: { count: number }) => plural(p.count, {
    one: `${p.count} other account uses this theme. Your change reaches it.`,
    other: `${p.count} other accounts use this theme. Your change reaches them.`,
  }),
  confirm_save: 'SAVE FOR ALL',
  contrast_bad: (p: { pair: string | number; ratio: string | number; floor: string | number }) => `${p.pair} ${p.ratio} : 1 · BELOW ${p.floor} : 1 · THE SAVE IS REFUSED`,
  contrast_ok: (p: { pair: string | number; ratio: string | number; floor: string | number }) => `${p.pair} ${p.ratio} : 1 · CLEARS ${p.floor} : 1`,
  copy_code: 'COPY CODE',
  discard: 'DISCARD',
  err_activate: (p: { message: string | number }) => `The theme is saved, but it did not become active: ${p.message}`,
  err_entitlement: 'Making custom themes is not in the plan of this account.',
  err_failed: (p: { message: string | number }) => `The save failed: ${p.message}`,
  err_impersonation: 'An operator session cannot save a theme.',
  err_invalid: (p: { field: string | number; reason: string | number }) => `The server refused ${p.field}: ${p.reason}.`,
  err_no_meta: (p: { message: string | number }) => `The account server did not answer: ${p.message}`,
  err_signed_out: 'The session ended. Sign in on the hub, then save again.',
  err_too_large: (p: { max: string | number }) => `The theme is larger than ${p.max} bytes. Remove clips or tracks, then save again.`,
  field_anchoring: 'ANCHORING',
  field_aura: 'SHIELD AURA',
  field_banner: 'SEASON BANNER',
  field_beam: 'HEAL BEAM',
  field_cache_work: 'CACHES',
  field_currents: 'CURRENTS',
  field_currents_heat: 'CURRENTS HEAT MAP',
  field_damage: 'DAMAGE FIGURES',
  field_deployable: 'DEPLOYABLE ICON',
  field_deployable_collect: 'DEPLOYABLE COLLECT',
  field_deployable_lay: 'DEPLOYABLE LAY',
  field_deposit: 'DEPOSIT ICON',
  field_depth: 'DEPTH',
  field_detonation: 'DETONATION',
  field_digits: 'GROUP DIGITS',
  field_dock: 'CHROME DOCK-IN',
  field_faction_edge: 'BORDER DASH',
  field_faction_hull: 'HULL PIP',
  field_faction_key: 'FACTION KEY',
  field_faction_site: 'HOLD CHARGE',
  field_gather: 'HARVESTING',
  field_hit_hull: 'HULL HITS',
  field_hit_shield: 'SHIELD HITS',
  field_hit_sink: 'FINAL BLOW',
  field_hold: 'HOLD SYMBOL',
  field_hold_fire: 'GUNS TIGHT',
  field_map_marker: 'MAP MARKER',
  field_notices: 'NOTICES',
  field_own_fleet: 'OWN FLEET MARK',
  field_own_hold: 'OWN HOLD MARK',
  field_poi: 'POINT OF INTEREST',
  field_reload: 'TUBE RELOAD',
  field_sea_mine: 'SEA MINE ICON',
  field_sea_mine_collect: 'SEA MINE COLLECT',
  field_sea_mine_detonation: 'SEA MINE DETONATION',
  field_sea_mine_lay: 'SEA MINE LAY',
  field_shield: 'SHIELD BAR',
  field_ship: 'SHIP ICON',
  field_shot_artillery: 'ARTILLERY SHOTS',
  field_shot_melee: 'CLOSE-IN SHOTS',
  field_shot_sentinel: 'TOWER SHOTS',
  field_spawn: 'SPAWN ANIMATION',
  field_squad_fleet: 'SQUAD FLEET MARK',
  field_squad_hold: 'SQUAD HOLD MARK',
  field_structure: 'STRUCTURE ICON',
  field_terrain: 'TERRAIN',
  field_territory: 'TERRITORY',
  field_torpedo: 'TORPEDO LOOK',
  fields_lede: 'Press a row to show it in the preview. A row with one look has a stepper that does not move.',
  gate_list_failed: 'YOUR THEMES DID NOT LOAD',
  gate_no_maker: 'A PREMIUM ACCOUNT MAKES CUSTOM THEMES',
  gate_no_maker_why: 'Making custom themes is not in the plan of this account. Every account with a Supporter plan uses a custom theme: type its code in THEMES of the MODDING section in the menu.',
  gate_no_meta: 'THE ACCOUNT SERVER DID NOT ANSWER',
  gate_premium_makes: 'A Premium plan makes one custom theme in each of the six theme categories: the colours of the interface, the music, the sound effects, the world looks, the map marks and the chart pieces. Players of your clan then use your themes through your code.',
  gate_see_plans: 'SEE THE PLANS',
  gate_signed_out: 'SIGN IN FIRST',
  gate_signed_out_why: 'The theme workshop needs an account. Sign in on the hub, then open the workshop again.',
  group_combat: 'COMBAT',
  group_deployables: 'DEPLOYABLES',
  group_faction: 'FACTION',
  group_interface: 'INTERFACE',
  group_map: 'MAP',
  group_support: 'SUPPORT',
  header_code: (p: { code: string | number }) => `YOUR CODE ${p.code}`,
  hulls_closed: 'PER HULL ▸',
  hulls_count: (p: { count: string | number }) => `${p.count} OVERRIDDEN`,
  hulls_lede: 'A hull row overrides the ship icon set for that hull. FOLLOW takes the set.',
  hulls_none: 'ALL FOLLOW',
  hulls_open: 'PER HULL ▾',
  hulls_overview: 'OVERVIEW',
  impersonated: 'AN OPERATOR SESSION CANNOT SAVE A THEME. THE WORKSHOP SHOWS THE THEMES AND SAVES NOTHING.',
  keep_mine: 'KEEP MINE',
  keep_range: 'KEEP COLOURS IN THE SUGGESTED RANGE',
  make_active: 'MAKE THEME ACTIVE',
  meta_none: 'NO THEME YET · THIS TAB STARTS FROM AUTO',
  meta_only_you: 'ONLY YOU USE THIS THEME',
  meta_others: (p: { count: number }) => plural(p.count, {
    one: `${p.count} OTHER ACCOUNT USES THIS THEME`,
    other: `${p.count} OTHER ACCOUNTS USE THIS THEME`,
  }),
  meta_theme: (p: { code: string | number; version: string | number }) => `CODE ${p.code} · VERSION ${p.version}`,
  meta_unused: 'NO ACCOUNT USES THIS THEME',
  move_down: 'MOVE DOWN',
  move_up: 'MOVE UP',
  moved: (p: { hex: string | number }) => `Moved to ${p.hex} to stay in range.`,
  music_empty: 'The list is empty. Add at least one track from the bank below.',
  music_lede: 'A Music theme is a list of tracks from the audio bank, in play order. Add tracks, move them and press ▶ to hear one.',
  music_list: (p: { count: string | number; max: string | number }) => `YOUR LIST · ${p.count} / ${p.max}`,
  no_bank: 'THIS HOST PUBLISHES NO AUDIO BANK',
  no_bank_why: 'The Music and the SFX tabs need the audio manifest of the host.',
  off: 'OFF',
  on: 'ON',
  open_hub: 'OPEN THE HUB',
  order_warn: (p: { lower: string | number; upper: string | number }) => `${p.lower} IS LIGHTER THAN ${p.upper}`,
  pair_faint_panel: 'FAINT TEXT ON PANEL',
  pair_faint_sheet: 'FAINT TEXT ON SHEET',
  pair_text_panel: 'TEXT ON PANEL',
  pick_label: (p: { row: string | number }) => `Pick the ${p.row} look`,
  reading_historical: 'HISTORICAL',
  reading_modern: 'MODERN',
  reason_count: 'the list has too few or too many entries',
  reason_missing: 'it is missing',
  reason_not_object: 'it must be an object',
  reason_pattern: 'the text has the wrong shape',
  reason_range: 'the number is outside its bounds',
  reason_syntax: 'the body is not JSON',
  reason_type: 'it has the wrong type',
  reason_unknown: 'it is not in the theme contract',
  remove: 'REMOVE',
  replay: 'REPLAY',
  restored: 'Restored your unsaved edits of this tab.',
  revert: 'REVERT TAB',
  row_tracks: 'YOUR LIST',
  row_voice: 'VOICE TIMING',
  sample: 'PLAY A SAMPLE',
  save: 'SAVE',
  save_off: (p: { pair: string | number; floor: string | number }) => `${p.pair} IS BELOW ${p.floor} : 1`,
  saved: (p: { code: string | number; version: string | number }) => `SAVED · CODE ${p.code} · VERSION ${p.version} · NEXT: COPY THE CODE FOR YOUR CLAN`,
  saved_active: (p: { code: string | number; version: string | number }) => `SAVED · CODE ${p.code} · VERSION ${p.version} · ACTIVE · NEXT: COPY THE CODE FOR YOUR CLAN`,
  saving: 'SAVING…',
  sfx_clip_cap: (p: { count: string | number; max: string | number }) => `${p.count} / ${p.max} CLIPS`,
  sfx_effect: 'EFFECT',
  sfx_groups: ['CORE SET', 'CONTACTS AND COMBAT', 'ECONOMY', 'ORDERS', 'TERRITORY AND SEASON', 'CAPTAINS', 'CLAN, HONOUR AND THE CLOCK', 'ACHIEVEMENTS', 'ARCADE · LOBBY AND COUNTDOWN', 'ARCADE · MATCH', 'CASINO', 'MENU'],
  sfx_lede: 'Pick the clips of each cue. A cue with no clip is silent in this theme. The game mutes some cues, and this tab does not show them.',
  sfx_no_clips: 'The bank holds no clip for this cue.',
  sfx_summary: (p: { fx: string | number; voice: string | number }) => `${p.fx} FX · ${p.voice} VOICE`,
  sfx_timing: 'TIMING',
  sfx_voice: 'VOICE',
  share_line: (p: { code: string | number }) => `Players of your clan type ${p.code} in THEMES of the MODDING section in the menu to use this theme.`,
  shuffle: 'SHUFFLE',
  started_from: (p: { name: string | number }) => `Started from ${p.name}.`,
  step_next: (p: { row: string | number }) => `Next ${p.row} look`,
  step_pos: (p: { n: string | number; count: string | number }) => `${p.n}/${p.count}`,
  step_prev: (p: { row: string | number }) => `Previous ${p.row} look`,
  stop_sample: 'STOP THE SAMPLE',
  tab_chart: 'CHART',
  tab_marks: 'MARKS',
  tab_music: 'MUSIC',
  tab_sfx: 'SFX',
  tab_ui: 'UI',
  tab_unsaved: 'unsaved changes',
  tab_world: 'WORLD',
  tabs_label: 'Theme categories',
  timing_theme: 'THEME',
  title: 'THEME WORKSHOP',
  token_accent: 'ACCENT',
  token_deep: 'DEEP INK',
  token_faint: 'FAINT TEXT',
  token_ink: 'PAGE INK',
  token_panel: 'PANEL',
  token_sheet: 'SHEET',
  token_text: 'TEXT',
  track_gone: 'NOT IN THE AUDIO BANK',
  ui_start: 'START FROM',
  undo: 'UNDO',
  unsaved_leave: (p: { count: number }) => plural(p.count, {
    one: `${p.count} tab holds changes you did not save. Press ← HUB again to leave without them.`,
    other: `${p.count} tabs hold changes you did not save. Press ← HUB again to leave without them.`,
  }),
  unsaved_tab: (p: { tab: string | number }) => `The ${p.tab} tab holds changes you did not save. They stay on this page until you save them or leave the page.`,
  val_hsl: 'HSL',
  val_hsl_value: (p: { h: string | number; s: string | number; l: string | number }) => `${p.h}°  ${p.s} %  ${p.l} %`,
  val_oklch: 'OKLCh',
  val_oklch_value: (p: { l: string | number; c: string | number }) => `L ${p.l}  C ${p.c}`,
  val_out: 'OUTSIDE THE RANGE',
  val_range: 'RANGE',
  val_range_band: (p: { lmin: string | number; lmax: string | number; cmin: string | number; cmax: string | number }) => `L ${p.lmin}–${p.lmax} · C ${p.cmin}–${p.cmax}`,
  val_range_ceiling: (p: { lmin: string | number; lmax: string | number; cmax: string | number }) => `L ${p.lmin}–${p.lmax} · C ≤ ${p.cmax}`,
  val_span: (p: { h: string | number; lmin: string | number; lmax: string | number }) => `(at ${p.h}°: HSL L ${p.lmin}–${p.lmax} %)`,
  val_span_none: (p: { h: string | number }) => `(at ${p.h}°: no sRGB colour is in the range)`,
  values_closed: 'COLOUR VALUES ▸',
  values_lede: 'Each token has a suggested range in OKLCh lightness and chroma. The hue is free. The range never refuses a save.',
  values_open: 'COLOUR VALUES ▾',
  voice_after: 'VOICE AFTER THE EFFECT',
  voice_delay: 'VOICE DELAY · MS',
};

const workshopLabels_en: S.WorkshopLabels = {
  cue_achievement: 'ACHIEVEMENT',
  cue_ambush_shot: 'AMBUSH SHOT',
  cue_arcade_base_fire: 'YOUR BASE FIRES',
  cue_arcade_buy: 'SHIP OR ROUND BOUGHT',
  cue_arcade_capture_broken: 'CAPTURE BROKEN',
  cue_arcade_capture_start: 'CAPTURE STARTED',
  cue_arcade_capture_threat: 'STRUCTURE UNDER CAPTURE',
  cue_arcade_clock_tick: 'LAST TEN SECONDS',
  cue_arcade_count: 'COUNTDOWN 3 2 1',
  cue_arcade_draw: 'DRAW',
  cue_arcade_fight: 'FIGHT',
  cue_arcade_hold: 'HOLD STARTS',
  cue_arcade_hold_broken: 'HOLD BROKEN',
  cue_arcade_kill: 'KILL CREDITED',
  cue_arcade_last_minute: 'LAST MINUTE',
  cue_arcade_lose: 'LOSS',
  cue_arcade_match_found: 'MATCH FOUND',
  cue_arcade_objective: 'WIN STEP',
  cue_arcade_queue_join: 'QUEUE JOINED',
  cue_arcade_queue_leave: 'QUEUE LEFT',
  cue_arcade_seat_loaded: 'SEAT LOADED',
  cue_arcade_sov_behind: 'ANOTHER TEAM TAKES THE LEAD',
  cue_arcade_sov_lead: 'YOUR TEAM TAKES THE LEAD',
  cue_arcade_spawn: 'AUTO SPAWN ARRIVES',
  cue_arcade_step_reset: 'STEP RESET',
  cue_arcade_surrender: 'SEAT SURRENDERED',
  cue_arcade_verdict: 'VERDICT FOR A SPECTATOR',
  cue_arcade_win: 'WIN',
  cue_auction_sold: 'MARKET SALE',
  cue_beacon_anchor: 'BEACON ANCHORS',
  cue_beacon_destroyed: 'BEACON DESTROYED',
  cue_beacon_hostile: 'HOSTILE BEACON ANCHORS',
  cue_beacon_pulse: 'BEACON PULSE',
  cue_blueprint_acquired: 'BLUEPRINT ACQUIRED',
  cue_blueprint_masterwork: 'MASTERWORK BLUEPRINT',
  cue_build_queued: 'BUILD QUEUED',
  cue_captain_lost: 'CAPTAIN CAPTURED',
  cue_captain_promoted: 'CAPTAIN PROMOTED',
  cue_captain_recruited: 'CAPTAIN RECRUITED',
  cue_captain_taken: 'ENEMY CAPTAIN TAKEN',
  cue_cargo_delivered: 'CARGO LANDED',
  cue_casino_ball: 'BALL DROPS',
  cue_casino_chip: 'STAKE PLACED',
  cue_casino_deal: 'CARD DEALT',
  cue_casino_jackpot: 'JACKPOT',
  cue_casino_lose: 'STAKE LOST',
  cue_casino_payout: 'BIG PAYOUT',
  cue_casino_reel_stop: 'REEL STOPS',
  cue_casino_seat: 'SEAT TAKEN',
  cue_casino_wheel: 'WHEEL TURNS',
  cue_casino_win: 'TABLE PAYS',
  cue_casino_your_turn: 'YOUR TURN',
  cue_cataclysm: 'NEW SEASON OPENS',
  cue_clan_hail: 'CLAN APPLICATION',
  cue_club_payout: 'CLUB SHARE PAID',
  cue_contact_broken: 'CONTACT BROKEN',
  cue_contact_lost: 'CONTACT LOST',
  cue_contact_new: 'NEW CONTACT',
  cue_course_plotted: 'COURSE PLOTTED',
  cue_day_night: 'DAY AND NIGHT',
  cue_deposit_depleted: 'DEPOSIT WORKED OUT',
  cue_enemy_destroyed: 'ENEMY DESTROYED',
  cue_expedition_departed: 'EXPEDITION DEPARTS',
  cue_expedition_returned: 'EXPEDITION RETURNS',
  cue_fleet_departs: 'FLEET DEPARTS',
  cue_fleet_fatigued: 'FLEET FATIGUED',
  cue_fleet_fight: 'YOUR GUNS HIT FIRST',
  cue_fleet_hit: 'LATER GUN HITS',
  cue_fleet_returned: 'FLEET DOCKS',
  cue_fleet_sink: 'FLEET SINKS',
  cue_guard_kill: 'GUARD KILLS A ROUND',
  cue_honour_awarded: 'HONOUR AWARDED',
  cue_hull_repaired: 'HULL REPAIRED',
  cue_insufficient_resources: 'NOT ENOUGH RESOURCES',
  cue_magazine_empty: 'MAGAZINE EMPTY',
  cue_map_arrival: 'MAP ARRIVAL',
  cue_menu_back: 'SHEET CLOSES',
  cue_menu_badge: 'AWARD OR UNLOCK',
  cue_menu_chat_recv: 'CHAT LINE ARRIVES',
  cue_menu_chat_send: 'CHAT LINE SENT',
  cue_menu_error: 'ACTION REFUSED',
  cue_menu_launch: 'YOU ENTER A WORLD',
  cue_menu_press: 'BUTTON PRESS',
  cue_menu_purchase: 'SHOP ORDER CONFIRMED',
  cue_menu_rename: 'RENAME ACCEPTED',
  cue_menu_signin: 'SIGN-IN',
  cue_menu_tab: 'TAB OR PAGE CHANGES',
  cue_menu_tick: 'STEPPER STEP',
  cue_menu_toggle: 'SWITCH CHANGES',
  cue_object_held: 'STRATEGIC OBJECT HELD',
  cue_object_lost: 'STRATEGIC OBJECT LOST',
  cue_order_ack: 'LINK UP',
  cue_order_refused: 'ORDER REFUSED',
  cue_quake_final: 'SCORES FINAL',
  cue_quake_settle: 'SEAQUAKE · SETTLEMENT',
  cue_quake_stage1: 'SEAQUAKE · FLOOR SHIFT',
  cue_quake_stage2: 'SEAQUAKE · WAR ECONOMY',
  cue_queue_complete: 'QUEUE JOB DONE',
  cue_redraft_done: 'REDRAFT COMPLETE',
  cue_reload_complete: 'RELOAD COMPLETE',
  cue_requirement_missing: 'REQUIREMENT MISSING',
  cue_research_complete: 'RESEARCH DONE',
  cue_round_select: 'ROUND SELECTED',
  cue_scan: 'FLEET SCAN',
  cue_sentinel_anchor: 'SENTINEL ANCHORS',
  cue_shields_down: 'SHIELDS DOWN',
  cue_ship_launched: 'SHIP READY',
  cue_siege_attack: 'SIEGE · ATTACK',
  cue_siege_defend: 'SIEGE · DEFEND',
  cue_siege_failed: 'SIEGE FAILED',
  cue_signature_order: 'SIGNATURE ORDER SPENT',
  cue_signature_ready: 'SIGNATURE ORDER READY',
  cue_sonar_sweep: 'BASE SONAR PING',
  cue_sov1_taken: 'A CELL FLIPS',
  cue_sov2_taken: 'A SITE IS CAPTURED',
  cue_squad_ping: 'SQUAD SIGNAL',
  cue_taking_damage: 'FIRST HIT TAKEN',
  cue_tech_unlocked: 'TECH UNLOCKED',
  cue_tender_anchor: 'TENDER ANCHORS',
  cue_territory_lost: 'TERRITORY LOST',
  cue_torpedo_explode: 'TORPEDO GOES OFF',
  cue_torpedo_fire: 'TORPEDO FIRED',
  cue_torpedo_incoming: 'TORPEDO INCOMING',
  cue_torpedo_repair: 'REPAIR ROUND',
  cue_torpor_burst: 'ROUND BURST',
  cue_trial_passed: 'TRIAL PASSED',
  cue_truce_ended: 'TRUCE ENDS',
  cue_truce_tick: 'TRUCE COUNTDOWN',
  cue_under_attack: 'UNDER ATTACK',
  cue_we_are_detected: 'WE ARE DETECTED',
  piece_banks: 'BANKS',
  piece_battle_marks: 'BATTLE MARKS',
  piece_bearing_lines: 'LINES OF BEARING',
  piece_border_band: 'BORDER BAND',
  piece_bottom_nature: 'SEABED LETTERS',
  piece_cables: 'CABLES',
  piece_cardinal_marks: 'GUT MARKS',
  piece_cartouche: 'CARTOUCHE',
  piece_clan_burgee: 'CLAN BURGEE',
  piece_coastal_profile: 'COASTAL PROFILE',
  piece_compass_rose: 'COMPASS ROSE',
  piece_current_arrows: 'CURRENTS',
  piece_depth_tints: 'DEPTH TINTS',
  piece_designations: 'SONAR DESIGNATIONS',
  piece_edge_seam: 'EDGE SEAM',
  piece_epicentre: 'EPICENTRE',
  piece_faction_key: 'FACTION KEY',
  piece_fairways: 'FAIRWAYS',
  piece_graticule: 'GRATICULE',
  piece_harbour_limits: 'HARBOUR LIMITS',
  piece_hazards: 'HAZARDS',
  piece_heraldry: 'HERALDRY',
  piece_here_be_monsters: 'HERE BE MONSTERS',
  piece_hold_site: 'OLD HOLD SITE',
  piece_imprint: 'IMPRINT',
  piece_inbox_pins: 'INBOX PINS',
  piece_isobaths: 'DEPTH LINES',
  piece_legend: 'LEGEND',
  piece_light_chars: 'LIGHT CHARACTERISTICS',
  piece_limit_line: 'LIMIT LINES',
  piece_listening_watch: 'LISTENING WATCH',
  piece_map_markers: 'MAP MARKERS',
  piece_margin_warning: 'MARGIN WARNING',
  piece_memorial_reef: 'MEMORIAL REEF',
  piece_neat_line: 'NEAT LINE',
  piece_picket_line: 'PICKET LINE',
  piece_place_names: 'PLACE NAMES',
  piece_points_of_interest: 'POINTS OF INTEREST',
  piece_pool_monument: 'HALL OF FAME MONUMENT',
  piece_range_rings: 'RANGE RINGS',
  piece_restricted: 'RESTRICTED AREAS',
  piece_ribbons: 'RIBBON BANNERS',
  piece_ship_tracks: 'SHIP TRACKS',
  piece_shipping_lanes: 'SHIPPING LANES',
  piece_shoal_stipple: 'SHOAL STIPPLE',
  piece_sonar_sweep: 'SONAR SWEEP',
  piece_soundings: 'SOUNDINGS',
  piece_speed_label: 'SPEED LABEL',
  piece_spoil_ground: 'SPOIL GROUND',
  piece_tidal_diamond: 'TIDAL DIAMOND',
  piece_torpedo_run: 'TORPEDO RUN',
  piece_water_lines: 'WATER LINES',
  piece_works: 'WORKS IN PROGRESS',
  piece_wrecks: 'WRECKS',
};

const world_en: S.World = {
  house_name: (p: { name: string | number }) => `House ${p.name}`,
  unclaimed: 'Unclaimed',
};

export const tables = {
  sonarlab: sonarlab_en,
  squad: squad_en,
  state: state_en,
  symbols: symbols_en,
  terrainlab: terrainlab_en,
  timelapse: timelapse_en,
  touchLog: touchLog_en,
  trials: trials_en,
  viewpanel: viewpanel_en,
  warcharts: warcharts_en,
  welcome: welcome_en,
  workshop: workshop_en,
  workshopLabels: workshopLabels_en,
  world: world_en,
} satisfies Partial<S.StringTables>;
